import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useRef, useState } from 'react';
import slugify from 'react-slugify';
import Tag from '../../../../components/Display/Tag';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import LanguageManagerGuard from '../../../../components/Language/LanguageManagerGuard';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useToastContext } from '../../../../contexts/ToastContext';
import { privateFetch } from '../../../../utils/apiHelper';
import { CONSTANT } from '../../../../utils/constants';
import { validateURL } from '../../../../utils/stringHelper';

function ResourcePanel({ isOpen, onClose, onCreate, resource, onUpdate, loading }) {
  const [type, setType] = useState('');
  const [status, setStatus] = useState(undefined);
  const [title, setTitle] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [buyUrl, setBuyUrl] = useState('');
  const [deezerId, setDeezerId] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(undefined);
  const [program, setProgram] = useState(undefined);
  const [programList, setProgramList] = useState([]);

  const { accountContext } = useAccountContext();
  const [imageLoading, setImageLoading] = useState(false);
  const { showToast } = useToastContext();

  const fileUploadRef = useRef(null);

  useEffect(() => {
    if (resource) {
      setType(resource.type);
      setStatus(resource.status);
      setTitle(resource.title);
      setVideoUrl(resource.video_url);
      setBuyUrl(resource.buy_url);
      setDeezerId(resource.deezer_id);
      setDescription(resource.description);
      setImage(resource.main_image);
      setProgram(resource.program);
    } else {
      setType(CONSTANT.melismes.resourceTypes[0].value);
      setStatus(undefined);
      setTitle('');
      setVideoUrl('');
      setBuyUrl('');
      setDeezerId('');
      setDescription('');
      setImage(undefined);
      setProgram(undefined);
    }
  }, [resource, isOpen]);

  useEffect(() => {
    fetchProgramList();
  }, [isOpen]);

  const fetchProgramList = async () => {
    await privateFetch('GET', 'melismes', `/cu/${accountContext.id}/program/`).then((res) => {
      setProgramList(res);
    });
  };

  const uploadImage = async (imageForm) => {
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/image/`,
      "L'image a bien été ajoutée",
      imageForm,
      false,
      true
    ).then((res) => {
      setImage(res);
    });
    setImageLoading(false);
  };

  const onUpload = (event) => {
    if (event.files[0].size > CONSTANT.maxImageSize) {
      showToast('warn', '10Mo max.');
      onClear();
    } else {
      setImageLoading(true);

      const file = event.files[0];
      const imageForm = new FormData();
      imageForm.append('name', slugify(file.name));
      imageForm.append('file', file);
      uploadImage(imageForm);
    }
  };

  const onClear = () => {
    setImage(undefined);
    fileUploadRef.current.clear();
  };

  const handleValidate = () => {
    const resourcePayload = {
      type,
      status,
      title,
      video_url: videoUrl,
      buy_url: buyUrl,
      deezer_id: deezerId,
      description,
      main_image: image?.id,
      program: program?.id
    };
    resource ? onUpdate(resourcePayload) : onCreate(resourcePayload);
  };

  const formIsValid = () => {
    return (
      title.length > 3 &&
      description.length > 3 &&
      image &&
      type &&
      validateURL(buyUrl) &&
      validateURL(videoUrl)
    );
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${resource ? 'modification' : 'création'}`}
        className="w-full"
        onClick={handleValidate}
        disabled={!formIsValid()}
        severity="success"
        loading={loading}
      />
    </div>
  );

  const dropdownTemplate = (option, props) => {
    if (option) {
      return (
        <Tag
          tag={{
            label: option.label,
            icon: option.icon,
            bgColor: option.bgColor
          }}
          rounded
        />
      );
    }
    return <span>{props.placeholder}</span>;
  };

  const programTemplate = (option, props) => {
    if (!option) return <span>{props.placeholder}</span>;
    return (
      <Tag
        tag={{
          label: option.title,
          bgColor: 'bg-slate-700',
          icon: 'program'
        }}
      />
    );
  };

  return (
    <Sidebar
      style={{ width: 600 }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${resource ? 'Modifier' : 'Ajouter'} une ressource`}</h3>
      }>
      <LanguageManagerGuard isCreation={!resource}>
        <div className="sidebar-content-container">
          <div className="form-wrapper">
            <FormInput label={'Type de ressource'} required isValid={type}>
              <Dropdown
                id="type"
                value={type}
                onChange={(e) => setType(e.value)}
                options={CONSTANT.melismes.resourceTypes}
                optionLabel="label"
                placeholder="Veuillez sélectionner le type de ressource"
                valueTemplate={dropdownTemplate}
                itemTemplate={dropdownTemplate}
              />
            </FormInput>

            <FormInput label={'Programme lié'}>
              <Dropdown
                id="program"
                value={program}
                onChange={(e) => setProgram(e.value)}
                options={programList}
                optionLabel="title"
                placeholder="Veuillez sélectionner le programme lié"
                filter={true}
                filterInputAutoFocus={true}
                valueTemplate={programTemplate}
                itemTemplate={programTemplate}
                dataKey="id"
                showClear
              />
            </FormInput>

            {resource && (
              <FormInput key="status" label={'État de la ressource'}>
                <Dropdown
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.value)}
                  options={CONSTANT.melismes.common.statusTypes}
                  optionLabel="label"
                  placeholder="Veuillez sélectionner l'état de la ressource"
                  valueTemplate={dropdownTemplate}
                  itemTemplate={dropdownTemplate}
                />
              </FormInput>
            )}
            <FormInput label={'Titre de la ressource'} required isValid={title.length > 3}>
              <InputText
                id="title"
                value={title}
                maxLength={300}
                placeholder="Veuillez saisir le titre de la ressource"
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormInput>
            <FormInput label={'URL de la vidéo'}>
              <InputText
                id="videoUrl"
                value={videoUrl}
                placeholder="Veuillez saisir l'url de la vidéo"
                onChange={(e) => setVideoUrl(e.target.value)}
              />
            </FormInput>
            <FormInput label={"URL d'achat"}>
              <InputText
                id="buyUrl"
                value={buyUrl}
                placeholder="Veuillez saisir l'url d'achat"
                onChange={(e) => setBuyUrl(e.target.value)}
              />
            </FormInput>
            <FormInput label={'ID Deezer du ressource'}>
              <InputText
                id="deezer_id"
                rows={3}
                autoResize
                value={deezerId}
                placeholder="Veuillez saisir l'ID Deezer"
                onChange={(e) => setDeezerId(e.target.value)}
              />
            </FormInput>

            <FormInput label={'Description'} required isValid={description.length > 3}>
              <RichTextEditor
                value={description}
                onChange={(value) => {
                  setDescription(value);
                }}
              />
            </FormInput>

            <FormInput key={'file'} label={`Photo principale (max. 10Mo)`} required isValid={image}>
              {image && (
                <img
                  src={image.file}
                  className="w-2/3 object-cover"
                  alt="illustration de la ressource"
                />
              )}
              <div className="flex flex-row gap-x-3 mt-3">
                {image && (
                  <Button
                    key={'delete-img'}
                    label={"Supprimer l'image"}
                    outlined
                    onClick={onClear}
                    severity="danger"
                  />
                )}
                {imageLoading ? (
                  <div className="w-[50px] h-[50px] p-1">
                    <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                  </div>
                ) : (
                  <FileUpload
                    ref={fileUploadRef}
                    auto
                    disabled={image}
                    mode="basic"
                    name="file"
                    accept="image/*"
                    customUpload
                    uploadHandler={onUpload}
                    chooseLabel="Depuis mon ordinateur"
                  />
                )}
              </div>
            </FormInput>
          </div>
          {footerButtons}
        </div>
      </LanguageManagerGuard>
    </Sidebar>
  );
}

export default ResourcePanel;
